/**
 * FAQ funnel attribution + A/B test plumbing.
 *
 * - Tallies every `faq_*` analytics event into sessionStorage so we can
 *   attach engagement context to `checkout_initiated` /
 *   `checkout_completed` events later in the funnel.
 * - Assigns a sticky A/B variant for booking-page FAQ snippet placement
 *   (step 1 vs final review) and fires a `faq_ab_assigned` event once.
 */
import { subscribeAnalytics, trackEvent } from "./analytics";

const ENG_KEY = "faq_engagement_v1";
const VARIANT_KEY = "faq_snippet_variant_v1";

export type FaqVariant = "step1" | "review";

export type FaqEngagement = {
  questions_opened: number;
  searches: number;
  filters_used: number;
  deeplinks_clicked: number;
  last_question?: string;
  last_source?: string;
};

const EMPTY: FaqEngagement = {
  questions_opened: 0,
  searches: 0,
  filters_used: 0,
  deeplinks_clicked: 0,
};

function readEngagement(): FaqEngagement {
  if (typeof window === "undefined") return { ...EMPTY };
  try {
    const raw = window.sessionStorage.getItem(ENG_KEY);
    if (!raw) return { ...EMPTY };
    return { ...EMPTY, ...(JSON.parse(raw) as FaqEngagement) };
  } catch {
    return { ...EMPTY };
  }
}

function writeEngagement(e: FaqEngagement) {
  try {
    window.sessionStorage.setItem(ENG_KEY, JSON.stringify(e));
  } catch {
    /* ignore */
  }
}

export function getFaqEngagement(): FaqEngagement {
  return readEngagement();
}

export function resetFaqEngagement() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(ENG_KEY);
  } catch {
    /* ignore */
  }
}

/** Flatten engagement for inclusion as analytics params. */
export function faqEngagementParams(prefix = "faq_") {
  const e = readEngagement();
  return {
    [`${prefix}questions_opened`]: e.questions_opened,
    [`${prefix}searches`]: e.searches,
    [`${prefix}filters_used`]: e.filters_used,
    [`${prefix}deeplinks_clicked`]: e.deeplinks_clicked,
    [`${prefix}last_question`]: e.last_question,
    [`${prefix}last_source`]: e.last_source,
    [`${prefix}variant`]: getFaqVariant(),
  };
}

/** Sticky A/B variant for FAQ snippet placement. */
export function getFaqVariant(): FaqVariant {
  if (typeof window === "undefined") return "step1";
  try {
    const existing = window.localStorage.getItem(VARIANT_KEY) as FaqVariant | null;
    if (existing === "step1" || existing === "review") return existing;
    const v: FaqVariant = Math.random() < 0.5 ? "step1" : "review";
    window.localStorage.setItem(VARIANT_KEY, v);
    // Fire once per assignment so the variant flows into GA.
    trackEvent("faq_ab_assigned", { variant: v });
    return v;
  } catch {
    return "step1";
  }
}

let installed = false;
/** Install the global analytics subscriber that tallies FAQ events. */
export function installFaqFunnelTally() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  subscribeAnalytics((evt) => {
    if (!evt.name.startsWith("faq_")) return;
    const e = readEngagement();
    switch (evt.name) {
      case "faq_question_open":
        e.questions_opened += 1;
        e.last_question = String(evt.params.question ?? "").slice(0, 120);
        e.last_source = String(evt.params.source ?? "");
        break;
      case "faq_search":
        e.searches += 1;
        break;
      case "faq_filter_select":
        e.filters_used += 1;
        break;
      case "faq_deeplink_click":
        e.deeplinks_clicked += 1;
        break;
      default:
        return;
    }
    writeEngagement(e);
  });
}